import _ from 'lodash';
import moment from 'moment';
import { isSubscribedToFeature } from './subscription_helpers';
import { getBrand, getShopifyIntegration } from './user_helpers';
import { faBox, faTruck, faStore, faPersonCarry, faCheck, faTimes, faBan } from '@fortawesome/pro-regular-svg-icons';
import { getDomainFromUrl } from './formatting';

export const getCartForLookbook = (lookbooks, lookbook) => lookbooks?.carts?.[lookbook.id] || {};
export const getCartItemsForLookbook = (lookbooks, lookbook) => {
  const lookbookCart = getCartForLookbook(lookbooks, lookbook);
  const selectedSiblingIds = _.keys(lookbookCart);
  let cartItems = [];
  lookbook.items?.forEach(item => {
    const siblingsInCart = item.siblings.filter(item => selectedSiblingIds.includes(String(item.id)));
    siblingsInCart.forEach(sibling => {
      cartItems.push({
        item,
        sibling,
        quantity: lookbookCart[sibling.id]
      });
    });
  });
  return cartItems;
};

export const getCartItemsForLookbookItem = (lookbooks, lookbook, item) => {
  const lookbookCart = getCartForLookbook(lookbooks, lookbook);
  const selectedSiblingIds = _.keys(lookbookCart);
  const siblingsInCart = item.siblings.filter(item => selectedSiblingIds.includes(String(item.id)));
  return siblingsInCart;
};

export const getAllLookbookOrderItems = requests => {
  const orderItems = [];
  for (const request of requests) {
    if (!request?.lookbook_order?.items?.length) continue;
    orderItems.push(...request.lookbook_order.items);
  }
  return orderItems;
};

/**
 * Helper function that will return an array of what siblings were selected
 * in orders for a given lookbook item. This is important because it is the
 * only way for brands to see how many of each size / color was selected
 *
 * @param {Object} item
 * @param {Object} item.reduced_siblings - array of high level siblings (has all size variations included in it)
 * @param {Object[]} orders
 */
export const getLookbookItemOrderSiblingBreakdown = (item, requests) => {
  if (!item?.reduced_siblings?.length) return [];
  else if (!requests?.length) return [];

  // if there is only one sibling, no need to calculate a breakdown
  if (item.reduced_siblings.length === 1) return [];

  const orders = getAllLookbookOrderItems(requests);

  // STEP 1 create a map of all the sibling ids that are associated to which larger sibling
  // [size_id, size_id, size_id] => reduced_sibling_id
  const { reduced_siblings } = item;
  const siblingIdMap = {};
  for (const reduced_sibling of reduced_siblings) {
    const size_variations = reduced_sibling?.size_variations || [];
    for (const size_variation of size_variations) siblingIdMap[size_variation.id] = reduced_sibling.id;
  }

  // STEP 2 iterate through all orders and create a map of sibling ids to quantity if they exist in the siblingIdMap
  const siblingQuantityMap = {};
  for (const order of orders) {
    const { LookbookItemSibling_id } = order;
    const isRelevantOrder = !!siblingIdMap[LookbookItemSibling_id];
    if (!isRelevantOrder) continue;

    const reducedSiblingId = siblingIdMap[LookbookItemSibling_id];
    if (!siblingQuantityMap[reducedSiblingId]) siblingQuantityMap[reducedSiblingId] = 0;
    siblingQuantityMap[reducedSiblingId] += 1;
  }

  // STEP 3 iterate through the keys of the siblingQuantityMap and create an array of objects
  // that contain the sibling and the quantity
  const siblingBreakdown = [];
  for (const siblingId in siblingQuantityMap) {
    const sibling = reduced_siblings.find(sibling => sibling.id === parseInt(siblingId));
    const quantity = siblingQuantityMap[siblingId];
    siblingBreakdown.push({ sibling, quantity });
  }

  return _.orderBy(siblingBreakdown, 'quantity', 'desc');
};

export const getLookbookItemInitialSibling = item => {
  const { reduced_siblings } = item || {};
  if (!reduced_siblings?.length) return null;
  return reduced_siblings.find(sibling => sibling.isDefault) || reduced_siblings.find(sibling => sibling.isAvailable) || reduced_siblings[0];
};

export const getLookbookItemStartingImage = item => {
  if (!item) return null;
  const initialSibling = getLookbookItemInitialSibling(item);
  return initialSibling?.image || item.image;
};

export const getLookbookItemPrice = LookbookItem =>
  LookbookItem?.overridePrice || LookbookItem?.defaultSibling?.price || LookbookItem?.fallbackPrice || LookbookItem?.variant?.price;

export const getLookbookItemIsCustom = LookbookItem =>
  !LookbookItem?.Variant_id &&
  !LookbookItem?.fallbackUrl &&
  !LookbookItem?.fallbackPrice &&
  !LookbookItem?.fallbackTitle &&
  !LookbookItem?.fallbackBrand &&
  !LookbookItem?.fallbackDescription;

export const getLookbookItemSelectedCount = (LookbookItem, lookbookCart) => {
  const { id } = LookbookItem;
  let count = 0;
  for (const key in lookbookCart) if (lookbookCart[key].item.id === id) count += lookbookCart[key].quantity;
  return count;
};

export const calculateLookbookCartItems = lookbookCart => {
  let cartItemCount = 0;
  let cartPrice = 0;
  for (const key in lookbookCart) {
    const { item, sibling, quantity } = lookbookCart[key];
    const price = sibling?.price || getLookbookItemPrice(item);
    cartPrice += price * quantity;
    cartItemCount += quantity || 1;
  }
  return { cartItemCount, cartPrice };
};

export const getDomainForLookbook = lookbook => {
  const siblings = _.flatten(lookbook.items.map(item => item.siblings));
  const hasNoSiblingUrls = siblings.every(sibling => !sibling.url);

  if (hasNoSiblingUrls) {
    // no siblings have urls, get domain from brand domain in getShopifyIntegration
    return undefined;
  }

  // at least some siblings have urls, get domain from those
  const siblingDomains = _.uniq(siblings.map(sibling => getDomainFromUrl(sibling.url))).filter(Boolean);
  return siblingDomains.length === 1 ? siblingDomains[0] : null;
};

export const isLookbookCompletelyConnectedToShopify = (user, lookbook) => {
  const siblings = _.flatten(lookbook.items.map(item => item.siblings));
  const siblingsCoveredByShopify = siblings.every(sibling => sibling.shopifyVariantId);
  const lookbookDomain = getDomainForLookbook(lookbook);

  const shopifyIntegration = getShopifyIntegration(user, lookbookDomain);

  return siblingsCoveredByShopify && shopifyIntegration.allowOrderCreation;
};

export const getLookbookOrderItemParts = item => {
  /*
    LookbookOrderItem.title is formatted as follows:
      name - nonSizeTag - sizeTag
  */
  const { title } = item;
  const name = title.split(' - ')[0];
  const nonSizeTag = title.split(' - ')[1];
  const sizeTag = title.split(' - ')[2];
  const pinDisplay = _.filter([name, nonSizeTag]).join(' - ');
  const siblingName = _.filter([sizeTag, nonSizeTag]).join(' - ');
  return {
    name,
    pinDisplay,
    nonSizeTag,
    sizeTag,
    siblingName
  };
};

export const getOrderHandledByData = (user, lookbook) => {
  /* 
    Orders can be handled in three ways:

    By Shopify - all siblings must have shopifyVariantIDs, same domain, and integration for domain enabled
    By ShopMy - brand must be on a high enough package
    By Brand - no requirements
 */
  const handledByOverride = lookbook?.orderHandledBy;
  const hasUnlockedShopMyHandling = isSubscribedToFeature(user, 'LOOKBOOK_SHOPMY_ORDER_HANDLING');

  const lookbookSiblings = _.flatten(lookbook.items.map(item => item.siblings));
  const siblingsConnectedToShopify = lookbookSiblings.every(sibling => sibling.shopifyVariantId);
  let lookbookDomain = getDomainForLookbook(lookbook);

  if (lookbookDomain === undefined) {
    // no siblings have urls, fallback to brand domain
    lookbookDomain = getBrand(user)?.domain || null;
  }

  const shopifyIntegration = getShopifyIntegration(user, lookbookDomain);
  const brandHasOrderCreationEnabled = shopifyIntegration.allowOrderCreation;
  // Core data variables
  let handledBy, reasoning, showReasoning, display;

  // Override case
  if (handledByOverride) {
    handledBy = handledByOverride;
    reasoning = 'Lookbook shipping has been explicitly set in the settings modal';
    display = handledByOverride === 'brand' ? 'Orders handled manually by your team' : 'Orders placed by ShopMy team';
    showReasoning = true;
  }

  // Shopify Case
  /*
  cases 
    1. Fully connected to shopify: siblings shopify connected, single lookbook item domain, shopify integration enabled for domain
    2. Partly disconnected from shopify: siblings shopify connected, null lookbook domain (multiple item domains present)
    3. Partly disconnected from shopify: siblings not all shopify connected, lookbook domain connected, shopify integration enabled for domain
  */
  else if (siblingsConnectedToShopify && lookbookDomain && brandHasOrderCreationEnabled) {
    handledBy = 'shopify';
    reasoning = lookbook.items.length
      ? 'Brand has order creation enabled in profile and all siblings have shopifyVariantIDs. Therefore orders can be automatically fulfilled by Shopify'
      : 'Brand has order creation enabled in profile';
    display = `Orders automatically placed in Shopify for ${lookbookDomain}`;
    showReasoning = false;
  } else if (siblingsConnectedToShopify && !lookbookDomain) {
    handledBy = 'brand';
    reasoning = 'Items in this lookbook contain multiple Shopify connected domains, therefore orders cannot be automatically fulfilled by Shopify';
    display = 'Orders handled manually by your team';
    showReasoning = true;
  } else if (!siblingsConnectedToShopify && lookbookDomain && brandHasOrderCreationEnabled) {
    handledBy = 'brand';
    reasoning = 'At least one item, variation or size is disconnected from Shopify';
    display = 'Orders fulfilled by you';
    showReasoning = true;
  }

  // ShopMy Case
  else if (hasUnlockedShopMyHandling) {
    handledBy = 'shopmy';
    reasoning = 'Brand is on a high enough package';
    display = 'Orders placed by ShopMy team';
    showReasoning = false;
  }

  // Brand Case
  else {
    handledBy = 'brand';
    reasoning = 'You are on a package that does not support order handling from the ShopMy team';
    display = 'Orders handled manually by your team';
    showReasoning = true;
  }

  return {
    handledBy,
    display,
    reasoning,
    showReasoning
  };
};

export const calculateOrderTotalItems = items => {
  if (!items?.length) return 0;
  let count = 0;
  for (const { quantity } of items) count += quantity;
  return count;
};

export const calculateOrderTotalPrice = items => {
  if (!items?.length) return 0;
  let price = 0;
  for (const { price: itemPrice, quantity } of items) price += itemPrice * quantity;
  return price;
};

export const filterInvalidLookbookItemSiblings = item => !item?.isHidden && item?.title && item.isAvailable;

export const getLookbookRequests = (analytics, lookbook) => {
  const { id: Lookbook_id } = lookbook;
  const allRequests = analytics?.brandAnalytics?.requests;
  const requests = allRequests?.filter(request => request.Lookbook_id === Lookbook_id);

  return requests?.length ? requests : [];
};

/**
 *
 * @param {Object} order
 * @param {string} order.status
 * @returns {Object} { display: string, icon: FontAwesomeIcon }
 */
export const getLookbookOrderStatusDisplay = order => {
  return (
    {
      submitted: {
        display: 'Submitted',
        icon: faBox
      },
      pending: {
        display: 'Submitted',
        icon: faTruck
      },
      shipped: {
        display: 'Shipped',
        icon: faTruck
      },
      ready_for_pickup: {
        display: 'Ready For Pickup',
        icon: faStore
      },
      out_for_delivery: {
        display: 'Out for Delivery',
        icon: faTruck
      },
      attempted_delivery: {
        display: 'Attempted Delivery',
        icon: faPersonCarry
      },
      delivered: {
        display: 'Delivered',
        icon: faCheck
      },
      failure: {
        display: 'Failure',
        icon: faTimes
      },
      cancelled: {
        display: 'Cancelled',
        icon: faBan
      }
    }[order?.status] || {
      display: 'Unknown',
      icon: faCheck
    }
  );
};

/**
 * Use this as a universal function to filter requests / mentions based on the date range
 * set by the user
 *
 * @param {Object} item - an item to filter (request or mention)
 * @param {'request'|'mention'} type - used to determine which item field to filter on
 * @param {String} startDate
 * @param {String} endDate
 */
export const lookbookReportItemFilter = (item, type, startDate, endDate) => {
  let filterField;

  if (type === 'request') filterField = 'userAcceptedAt';
  else if (type === 'mention') filterField = 'contentPublishedAt';

  if (!startDate && !endDate) return true;

  let [startDateQualifier, endDateQualifier] = [true, true];
  if (startDate) startDateQualifier = moment(item[filterField]).isSameOrAfter(startDate, 'day');
  if (endDate) endDateQualifier = moment(item[filterField]).isSameOrBefore(endDate, 'day');
  return startDateQualifier && endDateQualifier;
};
